// SPDX-License-Identifier: Elastic-2.0
/**
 * ThemeProvider — applies the resolved theme to `<html data-theme>`.
 *
 * Strategy:
 *   1. Subscribe to the `useTheme` store (user preference).
 *   2. When preference is "auto", subscribe to the OS `prefers-color-scheme`
 *      media query and react to live changes.
 *   3. Write `data-theme="dark"` or `data-theme="light"` on the root element.
 *
 * First-paint correctness is handled by the inline blocking script in
 * `index.html` (see `INTEGRATION.md`), not by this component — React
 * mounts after that script has already set the attribute, so there's
 * no flash. This component takes over for subsequent updates.
 *
 * Renders children unchanged. There is no React context here — every
 * consumer reads `useTheme` directly. Wrapping `<App />` is purely so
 * the effects mount at the right point in the tree lifecycle.
 */
import { useEffect } from "react";
import type { ReactNode } from "react";

import { useTheme } from "./useTheme";
import type { ThemeMode } from "./useTheme";

const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

type EffectiveTheme = "dark" | "light";

function prefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true; // default dark per spec
  }
  return window.matchMedia(DARK_MEDIA_QUERY).matches;
}

function resolveTheme(mode: ThemeMode): EffectiveTheme {
  if (mode === "dark") {
    return "dark";
  }
  if (mode === "light") {
    return "light";
  }
  return prefersDark() ? "dark" : "light";
}

function applyTheme(effective: EffectiveTheme): void {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.setAttribute("data-theme", effective);
}

export interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps): JSX.Element {
  const theme = useTheme((s) => s.theme);

  // Apply the effective theme whenever the user's preference changes.
  useEffect(() => {
    applyTheme(resolveTheme(theme));
  }, [theme]);

  // When in "auto" mode, react to live OS theme changes.
  useEffect(() => {
    if (theme !== "auto") {
      return;
    }
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(DARK_MEDIA_QUERY);
    const handler = (event: MediaQueryListEvent): void => {
      applyTheme(event.matches ? "dark" : "light");
    };
    // Safari < 14 only supports addListener/removeListener.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [theme]);

  return <>{children}</>;
}
